import React, { useEffect, useState } from "react"
import {
  ArrowLeft,
  Bookmark,
  BookOpen,
  FileCheck,
  FileText,
  Lightbulb,
  Printer,
  X,
} from "lucide-react"
import toast from "react-hot-toast"
import useQuestionAttempts from "../hooks/useQuestionAttempts"
import type { DetailTab, StudentQuestionDetail } from "../types"
import { printQuestionHtml } from "../utils/printQuestion"
import { stripHtml, firstSubPartLabel } from "../utils/mapQuestionCard"
import { displayableArtifacts } from "../../../../utils/generatedArtifacts"
import ResourceHtmlFrame from "./ResourceHtmlFrame"
import AttemptWorkspace from "./AttemptWorkspace"
import EmptyState from "./EmptyState"
import PersonalNotesTags from "./PersonalNotesTags"
import "./QuestionDetailPanel.style.scss"

interface QuestionDetailPanelProps {
  detail: StudentQuestionDetail | null
  loading: boolean
  error: string
  activeTab: DetailTab
  savingNote: boolean
  savingTag: boolean
  onTabChange: (tab: DetailTab) => void
  onClose: () => void
  onToggleBookmark: () => Promise<void>
  onSaveNote: (notes: string) => Promise<void>
  onAddTag: (tag: string) => Promise<void>
  onRemoveTag: (tag: string) => Promise<void>
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

/** Top-level answer HTML, or labelled sub-part answers when the root field is empty. */
const resolveAnswerContent = (
  detail: StudentQuestionDetail
): { available: boolean; content: string | null } => {
  if (stripHtml(detail.answer)) {
    return { available: true, content: detail.answer }
  }
  const parts = (detail.sub_parts || []).filter((part) => stripHtml(part.answer))
  if (!parts.length) return { available: false, content: null }
  const content = parts
    .map(
      (part) =>
        `<section class="sub-part-answer"><h4>${escapeHtml(
          part.label || "Part"
        )}</h4>${part.answer || ""}</section>`
    )
    .join("")
  return { available: true, content }
}

const QuestionDetailPanel = ({
  detail,
  loading,
  error,
  activeTab,
  savingNote,
  savingTag,
  onTabChange,
  onClose,
  onToggleBookmark,
  onSaveNote,
  onAddTag,
  onRemoveTag,
}: QuestionDetailPanelProps) => {
  const [noteDraft, setNoteDraft] = useState("")
  const hasScheme = !!detail?.resources.marking_scheme.available
  const canAiMark =
    hasScheme || !!detail?.resources.solution.available
  const answerResource = detail
    ? resolveAnswerContent(detail)
    : { available: false, content: null }
  const attemptsApi = useQuestionAttempts(detail?.id ?? null, {
    maxScore: detail?.marks || 5,
  })

  useEffect(() => {
    setNoteDraft(detail?.student?.notes || "")
  }, [detail?.id, detail?.student?.notes])

  const handleBookmark = async () => {
    try {
      await onToggleBookmark()
    } catch {
      // Parent (QuestionsPage) shows toast + reverts list sync.
    }
  }

  const detailMetaLines = (d: StudentQuestionDetail): string[] => [
    [d.year, d.paper || d.source, d.complexity].filter(Boolean).join(" · "),
    [
      d.topic_hierarchy?.topic,
      d.topic_hierarchy?.level_2_name,
      d.topic_hierarchy?.level_3_name,
      d.topic_hierarchy?.level_4_name,
    ]
      .filter(Boolean)
      .join(" › "),
  ]

  const handlePrintQuestion = () => {
    if (!detail) return
    const block = detail.resources.question
    if (!block.content?.trim() && !displayableArtifacts(block.artifacts).length) {
      toast.error("Nothing to print for this question")
      return
    }
    const label = firstSubPartLabel(detail.sub_parts)
    const qLabel = label ? `Q${label}` : "Question"
    printQuestionHtml({
      title: `${detail.subject_title || "Question"} — ${qLabel}`,
      html: block.content || "",
      artifacts: block.artifacts,
      metaLines: detailMetaLines(detail),
    })
  }

  /** Same print dialog as the list printer — user can Save as PDF from there. */
  const handleDownload = (
    kind: "question" | "answer" | "solution" | "marking-scheme",
    label: string,
    block?: {
      content: string | null
      pdf_url?: string | null
      artifacts?: StudentQuestionDetail["resources"]["question"]["artifacts"]
    }
  ) => {
    if (!detail) return
    const resource =
      block ||
      (kind === "question"
        ? detail.resources.question
        : kind === "solution"
          ? detail.resources.solution
          : detail.resources.marking_scheme)
    const html = (resource.content || "").trim()
    if (!html && !displayableArtifacts(resource.artifacts).length) {
      toast.error(`${label} is not available to download`)
      return
    }
    printQuestionHtml({
      title: `${detail.subject_title || "Question"} — ${label}`,
      html,
      artifacts: resource.artifacts,
      metaLines: detailMetaLines(detail),
    })
  }

  return (
    <aside className="question-detail-panel">
      <div className="question-detail-panel__header">
        <div className="question-detail-panel__header-left">
          <button
            type="button"
            className="question-detail-panel__back"
            onClick={onClose}
            aria-label="Back to list"
          >
            <ArrowLeft size={20} />
          </button>
          {detail && (
            <>
              <span className="question-detail-panel__subject">
                {detail.subject_title || "Subject"}
              </span>
              {detail.year != null && (
                <span className="question-detail-panel__year">{detail.year}</span>
              )}
            </>
          )}
          {!detail && loading && (
            <span className="question-detail-panel__year">Loading…</span>
          )}
        </div>
        <div className="question-detail-panel__header-actions">
          <button
            type="button"
            aria-label="Print question"
            onClick={handlePrintQuestion}
            disabled={!detail?.resources.question.available}
          >
            <Printer size={16} />
          </button>
          <button
            type="button"
            className={detail?.student?.is_bookmarked ? "is-on" : undefined}
            onClick={handleBookmark}
            disabled={!detail}
            aria-label="Toggle bookmark"
          >
            <Bookmark
              size={16}
              fill={detail?.student?.is_bookmarked ? "currentColor" : "none"}
            />
          </button>
          <button
            type="button"
            className="question-detail-panel__close"
            onClick={onClose}
            aria-label="Close detail"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="question-detail-panel__scroll">
        {loading && (
          <div className="question-detail-panel__pad">
            <EmptyState title="Loading question…" />
          </div>
        )}
        {!loading && error && (
          <div className="question-detail-panel__pad">
            <EmptyState title="Couldn’t load question" description={error} />
          </div>
        )}
        {!loading && !error && detail && (
          <>
            <div className="question-detail-panel__pad">
              <ResourceHtmlFrame
                title="Question Document"
                icon={<FileText size={16} />}
                available={detail.resources.question.available}
                content={detail.resources.question.content}
                artifacts={detail.resources.question.artifacts}
                tall
                onDownload={() =>
                  handleDownload("question", "Question Document")
                }
                downloadDisabled={
                  !detail.resources.question.content &&
                  !detail.resources.question.pdf_url &&
                  !displayableArtifacts(detail.resources.question.artifacts)
                    .length
                }
              />

              <div className="question-detail-panel__tabs" role="tablist">
                {(
                  [
                    ["attempts", "My Attempts", true],
                    ["answer", "Answer", answerResource.available],
                    [
                      "solution",
                      "Teacher Solution",
                      detail.resources.solution.available,
                    ],
                    [
                      "markscheme",
                      "Marking Scheme",
                      detail.resources.marking_scheme.available,
                    ],
                  ] as Array<[DetailTab, string, boolean]>
                ).map(([id, label, enabled]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === id}
                    className={activeTab === id ? "is-active" : ""}
                    disabled={!enabled}
                    onClick={() => onTabChange(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="question-detail-panel__tab-body">
                {activeTab === "attempts" && (
                  <AttemptWorkspace
                    attempts={attemptsApi.attempts}
                    maxAttempts={attemptsApi.maxAttempts}
                    canStartNew={attemptsApi.canStartNew}
                    hasMarkingScheme={hasScheme}
                    canAiMark={canAiMark}
                    onStart={attemptsApi.startAttempt}
                    onUpdateDraft={attemptsApi.updateDraft}
                    onSubmit={attemptsApi.submitAttempt}
                  />
                )}

                {activeTab === "answer" && (
                  <ResourceHtmlFrame
                    title="Answer"
                    icon={<Lightbulb size={16} className="is-blue" />}
                    available={answerResource.available}
                    content={answerResource.content}
                    emptyLabel="An answer hasn't been uploaded for this question yet."
                    tall
                    onDownload={() =>
                      handleDownload("answer", "Answer", {
                        content: answerResource.content,
                        pdf_url: null,
                      })
                    }
                    downloadDisabled={!answerResource.content}
                  />
                )}

                {activeTab === "solution" && (
                  <ResourceHtmlFrame
                    title="Official Solution"
                    icon={<BookOpen size={16} className="is-blue" />}
                    available={detail.resources.solution.available}
                    content={detail.resources.solution.content}
                    artifacts={detail.resources.solution.artifacts}
                    emptyLabel="A teacher solution hasn't been uploaded for this question yet."
                    tall
                    onDownload={() =>
                      handleDownload("solution", "Teacher Solution")
                    }
                    downloadDisabled={
                      !detail.resources.solution.content &&
                      !detail.resources.solution.pdf_url &&
                      !displayableArtifacts(detail.resources.solution.artifacts)
                        .length
                    }
                  />
                )}

                {activeTab === "markscheme" && (
                  <ResourceHtmlFrame
                    title="Marking Scheme"
                    icon={<FileCheck size={16} className="is-green" />}
                    available={detail.resources.marking_scheme.available}
                    content={detail.resources.marking_scheme.content}
                    emptyLabel="The official marking scheme is not available for this question."
                    tall
                    onDownload={() =>
                      handleDownload("marking-scheme", "Marking Scheme")
                    }
                    downloadDisabled={
                      !detail.resources.marking_scheme.content &&
                      !detail.resources.marking_scheme.pdf_url
                    }
                  />
                )}
              </div>
            </div>

            <PersonalNotesTags
              questionId={detail.id}
              note={noteDraft}
              savedNote={detail.student?.notes || ""}
              tags={detail.student?.tags || []}
              savingNote={savingNote}
              savingTag={savingTag}
              onNoteChange={setNoteDraft}
              onSaveNote={() => onSaveNote(noteDraft)}
              onClearNote={() => onSaveNote("")}
              onAddTag={onAddTag}
              onRemoveTag={onRemoveTag}
            />
          </>
        )}
      </div>
    </aside>
  )
}

export default QuestionDetailPanel
